import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { useAuthenticatedFetch } from '@/hooks/useAuthenticatedFetch';
import { formatTimestamp } from '@/lib/formatters';
import type { AdminUserLogsResponse, AdminLogEvent } from '@/types/admin';

const TIME_PRESETS = [
  { label: 'Last 15 minutes', value: '15m' },
  { label: 'Last 1 hour', value: '1h' },
  { label: 'Last 6 hours', value: '6h' },
  { label: 'Last 24 hours', value: '24h' },
  { label: 'Last 3 days', value: '3d' },
  { label: 'Last 7 days', value: '7d' },
];

function presetToRange(preset: string): { start: number; end: number } {
  const now = Date.now();
  const presetMs: Record<string, number> = {
    '15m': 15 * 60 * 1000,
    '1h': 60 * 60 * 1000,
    '6h': 6 * 60 * 60 * 1000,
    '24h': 24 * 60 * 60 * 1000,
    '3d': 3 * 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
  };
  return { start: now - (presetMs[preset] ?? 60 * 60 * 1000), end: now };
}

function tryParseJson(msg: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(msg);
    if (typeof parsed === 'object' && parsed !== null) return parsed as Record<string, unknown>;
  } catch {
    // ignore non-JSON messages
  }
  return null;
}

function LogEventRow({ event }: { event: AdminLogEvent }) {
  const [expanded, setExpanded] = useState(false);
  const parsed = tryParseJson(event.message);
  const level = typeof parsed?.level === 'number' ? parsed.level : null;
  const msg = typeof parsed?.msg === 'string' ? parsed.msg : event.message;

  const levelVariant =
    level != null
      ? level >= 50
        ? 'destructive'
        : level >= 40
          ? 'secondary'
          : 'outline'
      : 'outline';

  const levelLabel =
    level != null
      ? level >= 50
        ? 'ERROR'
        : level >= 40
          ? 'WARN'
          : level >= 30
            ? 'INFO'
            : 'DEBUG'
      : null;

  return (
    <div
      className="border-b last:border-0 px-3 py-2 hover:bg-muted/30 cursor-pointer"
      onClick={() => setExpanded((v) => !v)}
    >
      <div className="flex items-start gap-2">
        <span className="font-mono text-xs text-muted-foreground whitespace-nowrap pt-0.5">
          {formatTimestamp(event.timestamp)}
        </span>
        {levelLabel && <Badge variant={levelVariant} className="text-xs shrink-0">{levelLabel}</Badge>}
        <span className="font-mono text-xs flex-1 truncate">{msg}</span>
      </div>
      {expanded && (
        <pre className="mt-2 text-xs bg-muted rounded p-2 overflow-x-auto whitespace-pre-wrap break-all font-mono text-foreground">
          {parsed ? JSON.stringify(parsed, null, 2) : event.message}
        </pre>
      )}
    </div>
  );
}

export default function UserLogsPage() {
  const { userId } = useParams<{ userId: string }>();
  const authenticatedFetch = useAuthenticatedFetch();
  const [data, setData] = useState<AdminUserLogsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timePreset, setTimePreset] = useState('1h');
  const [filterText, setFilterText] = useState('');
  const [limit, setLimit] = useState('200');

  const fetchLogs = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const { start, end } = presetToRange(timePreset);
      const params = new URLSearchParams({
        startTime: String(start),
        endTime: String(end),
        limit,
      });
      if (filterText.trim()) params.set('filter', filterText.trim());

      const res = await authenticatedFetch(`users/${userId}/logs?${params}`);
      if (!res.ok) throw new Error(`Failed to load logs (${res.status})`);
      setData((await res.json()) as AdminUserLogsResponse);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const errorEvents = data?.events.filter((e) => {
    const p = tryParseJson(e.message);
    return typeof p?.level === 'number' && (p.level as number) >= 50;
  });

  return (
    <>
      <LoadingOverlay visible={loading} message="Fetching CloudWatch logs..." />
      <div className="space-y-4 sm:space-y-6">
        <Breadcrumb
          items={[
            { label: 'Dashboard', to: '/' },
            { label: 'Users', to: '/users' },
            { label: userId ?? 'User', to: `/users/${userId}` },
            { label: 'Logs' },
          ]}
        />
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">CloudWatch logs</h1>
            <p className="text-muted-foreground font-mono text-xs mt-1">{userId}</p>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/users/${userId}/cost-audit`}>Cost Audit →</Link>
          </Button>
        </div>

        {/* Controls */}
        <Card>
          <CardHeader>
            <CardTitle>Query parameters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>Time range</Label>
                <Select value={timePreset} onValueChange={setTimePreset}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_PRESETS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Custom filter pattern</Label>
                <Input
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder={`{ $.userId = "${userId}" }`}
                  className="font-mono text-xs"
                />
              </div>
              <div className="space-y-2">
                <Label>Limit</Label>
                <Select value={limit} onValueChange={setLimit}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['50', '100', '200', '500'].map((v) => (
                      <SelectItem key={v} value={v}>{v} events</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <Button onClick={fetchLogs} disabled={loading}>
                {loading ? 'Fetching…' : 'Fetch logs'}
              </Button>
              {data && (
                <span className="text-sm text-muted-foreground">
                  {data.scannedCount} events · log group: <code className="font-mono text-xs">{data.logGroup}</code>
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {data?.queryNote && (
          <div className="rounded-md border border-yellow-500/50 bg-yellow-500/5 p-3 text-sm text-yellow-700">
            {data.queryNote}
          </div>
        )}

        {data && errorEvents && errorEvents.length > 0 && (
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive">Errors ({errorEvents.length})</CardTitle>
              <CardDescription>Error-level log events in the selected window</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="rounded-md overflow-hidden">
                {errorEvents.map((event, idx) => (
                  <LogEventRow key={idx} event={event} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Log events</CardTitle>
            <CardDescription>
              {data
                ? `${data.events.length} events · ${formatTimestamp(data.startTime)} → ${formatTimestamp(data.endTime)}`
                : 'CloudWatch Logs filtered by userId'}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {data && data.events.length > 0 ? (
              <div className="rounded-md overflow-hidden">
                {data.events.map((event, idx) => (
                  <LogEventRow key={idx} event={event} />
                ))}
                {data.hasMore && (
                  <div className="px-3 py-2 text-sm text-muted-foreground border-t">
                    More events available — increase limit or narrow the time range.
                  </div>
                )}
              </div>
            ) : (
              data && !loading && (
                <div className="px-4 py-6 text-sm text-muted-foreground text-center">
                  No log events found for this user in the selected time range.
                </div>
              )
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
